import { Options } from '@contentful/rich-text-react-renderer';
import { INLINES, BLOCKS } from '@contentful/rich-text-types';
import {
  Heading,
  Image,
  ListItem,
  Text,
  UnorderedList,
  StyleProps,
} from '@everlywell/ui-kit';
import AnchorLink from 'components/AnchorLink';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { ReactNode } from 'react';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';

import * as S from './styles';

type RichTextWrapperProps = {
  contentfulRichText: ContentfulRichText;
  options?: {
    references?: Array<{
      contentful_id: string;
      gatsbyImageData: IGatsbyImageData | null;
    } | null> | null;
    overrideOptions?: Options;
    removeWrapper?: boolean;
    styleProps?: StyleProps;
  };
};

const isValidURI = (url: string) =>
  url.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g,
  ) !== null;

const isQueryParamOnly = (uri: string) =>
  uri.match(/\?[-a-zA-Z0-9@:%._+~#=]{1,256}=[-a-zA-Z0-9@:%._+~#=]{1,256}/) !==
  null;

const RichTextWrapper = ({
  contentfulRichText,
  options: { references, overrideOptions, removeWrapper, styleProps } = {},
}: RichTextWrapperProps): JSX.Element => {
  // This is needed to overide the default behavior of the render. We want to open the links on another tab when they are not anchor links
  const richTextOptions: Options = {
    ...overrideOptions,
    renderNode: {
      [INLINES.HYPERLINK]: (node): ReactNode => {
        /* @ts-ignore (ignoring this because the link text comes in the content array)*/
        const linkText = node.content[0].value;

        if (isValidURI(node.data.uri)) {
          return (
            <a href={`${node.data.uri}`} target="_blank" rel="noreferrer">
              {linkText}
            </a>
          );
        } else if (isQueryParamOnly(node.data.uri)) {
          return <a href={`${node.data.uri}`}>{linkText}</a>;
        } else {
          return (
            <AnchorLink href={`#${node.data.uri}`} offset="50">
              {linkText}
            </AnchorLink>
          );
        }
      },
      [BLOCKS.UL_LIST]: (_, children) => (
        <UnorderedList styleType="initial" sx={{ marginInlineStart: '1.5em' }}>
          <ListItem {...styleProps}>{children}</ListItem>
        </UnorderedList>
      ),
      [BLOCKS.HEADING_1]: (_, children) => (
        <Heading as="h1" size="3xl" marginBottom={3} {...styleProps}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_2]: (_, children) => (
        <Heading as="h2" size="2xl" marginBottom={3} {...styleProps}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_3]: (_, children) => (
        <Heading as="h3" size="xl" marginBottom={3} {...styleProps}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_4]: (_, children) => (
        <Heading as="h4" size="lg" marginBottom={3} {...styleProps}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_5]: (_, children) => (
        <Heading as="h5" size="md" marginBottom={3} {...styleProps}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_6]: (_, children) => (
        <Text size="lg" marginBottom={3} {...styleProps}>
          {children}
        </Text>
      ),
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Text as="p" marginBottom={3} {...styleProps}>
          {children}
        </Text>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const id = node.data.target.sys.id;
        const linkType = node.data.target.sys.linkType;
        if (linkType !== 'Asset') return null;

        const asset = references?.find((ref) => ref?.contentful_id === id);
        if (!asset) return null;

        const { gatsbyImageData } = asset;
        if (!gatsbyImageData) return null;

        return (
          <Image
            as={GatsbyImage}
            image={gatsbyImageData}
            alt={''}
            marginBottom={3}
          />
        );
      },
      ...overrideOptions?.renderNode,
    },
  };

  if (removeWrapper) {
    return <>{renderRichText(contentfulRichText, richTextOptions)}</>;
  } else {
    return (
      <S.Wrapper>
        {renderRichText(contentfulRichText, richTextOptions)}
      </S.Wrapper>
    );
  }
};

export default RichTextWrapper;
